/**
 * LA VUE HTML ISOLEE (chantier `fiche-suivi`, 27/10/2026, option C de l'etude
 * des vues custom du 26/10 ; `docs/socle/fiche-suivi.md`, section 4). Une vue
 * de fiche ecrite en HTML, deposee par le MCP dans la declaration de l'org, et
 * rendue ici dans un CADRE FERME : elle voit la fiche, elle ne touche a rien.
 *
 * LE CADRE :
 * - `<iframe sandbox="allow-scripts">`, et RIEN d'autre dans le sandbox :
 *   jamais `allow-same-origin` (le cadre aurait l'origine de l'app, donc sa
 *   session et sa base), ni `allow-top-navigation`, `allow-popups`,
 *   `allow-forms`. Son origine est OPAQUE : ni cookie, ni stockage, ni session.
 * - charge sur une ROUTE STATIQUE de la meme origine
 *   (`ADRESSE_DU_CADRE_VUE_HTML`, `apps/hello/public/cadre-ferme/vue-html.html`),
 *   jamais par `srcdoc` ni `blob:` : ces deux-la HERITENT de la politique de
 *   contenu de l'app, qui bloquera tout script en ligne
 *   (`docs/socle/entetes-securite.md`, « Une vue HTML en cadre ferme »). La
 *   route a ses propres en-tetes (`firebase.json`, bloc `/cadre-ferme/**`).
 * - la page du cadre recoit le document par `postMessage` et le devient ; il
 *   porte une politique de contenu posee EN TETE de `<head>`, avant tout script
 *   de la vue (`documentDeVueHtml`) : aucun reseau (`connect-src 'none'`,
 *   `default-src 'none'`), images et polices en `data:` seulement, scripts et
 *   styles en ligne seulement. Elle s'ajoute a celle de la route.
 * - `referrerPolicy="no-referrer"` : le cadre ne dit pas d'ou il vient.
 *
 * LE CHARGEMENT, entre le gabarit et la page du cadre, avant la vue : la page
 * annonce `{ taki: 'vue-html', type: 'cadre' }`, le gabarit lui envoie UNE
 * fois `{ taki: 'vue-html', type: 'document', html }` AVEC LE PORT D'UN
 * `MessageChannel` (transfere), puis les donnees PAR CE PORT, et par lui seul.
 * La page garde le port avant de devenir le document de la vue, et relaie
 * chaque message du port a sa fenetre, en evenement `message` venu de la
 * fenetre parente : la vue ne voit ni le document, ni le port.
 *
 * POURQUOI UN PORT ET PAS `postMessage(..., '*')` (audit du 27/10) : la vue
 * peut NAVIGUER SON CADRE (`location = ...`, que le sandbox n'interdit pas a
 * un cadre sur lui-meme). La fenetre du cadre reste alors le meme objet, mais
 * sa page n'est plus la notre, et un envoi a `'*'` a chaque `load` lui donnait
 * la fiche. Un port, lui, meurt avec la page qui le tenait : une page arrivee
 * par navigation ne l'a jamais. Et le gabarit COUPE tout des qu'un chargement
 * inattendu montre que le cadre a quitte sa page (`lierLeCadre`).
 *
 * LE PROTOCOLE v1, LE CONTRAT QUE LES VUES DEPOSEES TIENNENT. Il ne casse
 * jamais : une v2 s'AJOUTE (un autre `version`), la v1 reste servie aux vues
 * qui la parlent.
 *
 * 1. Le gabarit -> la vue, un evenement `message` sur la fenetre du cadre
 *    (`event.source` : la fenetre parente, `event.origin` : celle de l'app,
 *    comme un `postMessage` du parent), au chargement du cadre, puis a chaque
 *    changement des donnees :
 *
 *      { taki: 'vue-html', version: 1, type: 'donnees', donnees, theme }
 *
 *    - `donnees` : ce que l'hote passe (la fiche ouverte et ses fiches liees,
 *      reduites aux champs que la vue declare), en lecture seule ;
 *    - `theme` : `{ '--accent': '#cbfb45', '--font-sans': 'Inter, ...', ... }`,
 *      les variables CSS du socle (`VARIABLES_DU_THEME`), a poser telles
 *      quelles (`document.documentElement.style.setProperty(nom, valeur)`).
 *
 *    Le message passe par le port puis par le relais de la page du cadre : la
 *    vue le recoit exactement comme avant ce relais.
 *
 * 2. La vue -> le gabarit : un seul message ecoute,
 *
 *      { taki: 'vue-html', type: 'pret' }
 *
 *    qui redemande l'envoi (une vue qui installe son ecoute apres le
 *    chargement ne rate pas les donnees). Tout autre message, et tout message
 *    qui ne vient pas de la fenetre de ce cadre, est ignore : la vue ne peut
 *    rien demander d'autre, ni ecrire, ni naviguer dans l'app.
 */
import { useEffect, useRef } from 'react';
import type { JSX } from 'react';

/** La marque des messages du protocole, dans les deux sens. */
export const MARQUE_VUE_HTML = 'vue-html';

/** La version du protocole que ce gabarit parle. */
export const VERSION_VUE_HTML = 1;

/** La page du cadre, servie par l'hote avec ses propres en-tetes (`firebase.json`). */
export const ADRESSE_DU_CADRE_VUE_HTML = '/cadre-ferme/vue-html.html';

/**
 * LA POLITIQUE DE CONTENU du cadre : rien par defaut, scripts et styles en
 * ligne (la vue tient dans un fichier), images et polices embarquees, aucun
 * reseau, aucun formulaire, aucune base.
 */
export const CSP_VUE_HTML =
  "default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data: blob:; font-src data:; connect-src 'none'; form-action 'none'; base-uri 'none'";

/**
 * LES VARIABLES DU SOCLE passees a la vue par defaut (`theme`) : l'accent de
 * l'org, l'encre, la surface, la police. Lues sur `document.documentElement`,
 * elles suivent donc le branding de l'org et le theme.
 */
export const VARIABLES_DU_THEME = [
  '--accent',
  '--accent-label',
  '--accent-tint',
  '--accent-ink',
  '--ink-900',
  '--ink-500',
  '--surface',
  '--border',
  '--font-sans',
] as const;

const META_CSP = `<meta http-equiv="Content-Security-Policy" content="${CSP_VUE_HTML}">`;

/* Ce qui peut preceder `<head>` sans rien executer : le doctype, des
   commentaires, l'ouverture de `<html>`. Autre chose (un script pose avant
   `<head>`) s'executerait AVANT la politique : le document est alors
   enveloppe, et ses balises de tete, ignorees par le navigateur dans un
   `<body>`, ne passent plus devant elle. */
const AVANT_HEAD_INOFFENSIF = /^\s*(?:<!doctype[^>]*>\s*)?(?:<!--[\s\S]*?-->\s*)*(?:<html(?:\s[^>]*)?>\s*)?(?:<!--[\s\S]*?-->\s*)*$/i;

/**
 * LE DOCUMENT D'UNE VUE HTML, tel que la page du cadre le devient : le
 * document de la vue, la politique de contenu posee EN TETE de son `<head>`.
 * Sans `<head>` exploitable, le document est enveloppe dans un document dont la
 * tete porte la politique. Pure : un test la tient sans navigateur.
 */
export function documentDeVueHtml(html: string): string {
  const tete = /<head(?:\s[^>]*)?>/i.exec(html);
  if (tete !== null && AVANT_HEAD_INOFFENSIF.test(html.slice(0, tete.index))) {
    const fin = tete.index + tete[0].length;
    return `${html.slice(0, fin)}${META_CSP}${html.slice(fin)}`;
  }
  return `<!doctype html><html><head>${META_CSP}<meta charset="utf-8"></head><body>${html}</body></html>`;
}

/** Le theme par defaut : les variables du socle, lues sur la racine du document. */
export function themeDuSocle(): Record<string, string> {
  if (typeof document === 'undefined') return {};
  const styles = getComputedStyle(document.documentElement);
  const theme: Record<string, string> = {};
  for (const nom of VARIABLES_DU_THEME) {
    const valeur = styles.getPropertyValue(nom).trim();
    if (valeur !== '') theme[nom] = valeur;
  }
  return theme;
}

/** Le message de donnees du protocole v1. */
export function messageDeDonnees(donnees: unknown, theme: Record<string, string>) {
  return { taki: MARQUE_VUE_HTML, version: VERSION_VUE_HTML, type: 'donnees', donnees, theme } as const;
}

/** Le message qui donne son document a la page du cadre. */
export function messageDeDocument(html: string) {
  return { taki: MARQUE_VUE_HTML, type: 'document', html: documentDeVueHtml(html) } as const;
}

function estUnMessage(data: unknown, type: string): boolean {
  if (data == null || typeof data !== 'object') return false;
  const message = data as { taki?: unknown; type?: unknown };
  return message.taki === MARQUE_VUE_HTML && message.type === type;
}

/** Vrai pour le message de la vue qui redemande les donnees : `{ taki: 'vue-html', type: 'pret' }`. */
export function estUnMessagePret(data: unknown): boolean {
  return estUnMessage(data, 'pret');
}

/** Vrai pour l'annonce de la page du cadre, avant la vue : `{ taki: 'vue-html', type: 'cadre' }`. */
export function estUnMessageCadre(data: unknown): boolean {
  return estUnMessage(data, 'cadre');
}

/**
 * CE QUE LA LIAISON DEMANDE AU CADRE : sa fenetre (lue au moment d'envoyer, le
 * cadre peut ne pas etre monte), ce qu'il faut envoyer (lu au moment d'envoyer
 * aussi : les donnees a l'ecran, pas celles d'un rendu passe), et de quoi
 * fabriquer le canal. Un test passe des faux ; le composant, le vrai DOM.
 */
export interface CadreALier {
  fenetre(): Pick<Window, 'postMessage'> | null | undefined;
  lire(): { html: string; donnees: unknown; theme: Record<string, string> };
  canal?: () => MessageChannel;
  /** Ce que la coupure dit, pour qui relit la console. */
  signaler?: (texte: string) => void;
}

/** L'etat d'une liaison : rien donne, document et port donnes, coupee. */
export type EtatDeLiaison = 'attente' | 'donne' | 'coupe';

export interface LiaisonDuCadre {
  /** La page du cadre s'annonce (`cadre`) : le document et le port partent, une fois. */
  annonce(): void;
  /** Un `load` de l'iframe. */
  charge(): void;
  /** La vue redemande (`pret`), ou les donnees ont change : elles partent par le port. */
  envoyer(): void;
  /** Le cadre se demonte : le port se ferme. */
  fermer(): void;
  etat(): EtatDeLiaison;
}

/** La phrase de la coupure (console). */
export const RAISON_CADRE_COUPE =
  '[taki/vue-html] Le cadre a quitte sa page : plus rien ne lui est envoye.';

/**
 * LA LIAISON ENTRE LE GABARIT ET UN CADRE, sortie du composant pour qu'un test
 * la tienne sans navigateur.
 *
 * LES CHARGEMENTS ATTENDUS SONT DEUX : la page du cadre (le premier `load`),
 * puis le document qu'elle devient (`document.open/write/close` refait un
 * `load` de l'iframe, HTML, « completely finish loading »). Le premier sert de
 * filet a l'annonce (s'il passe avant elle, la liaison se donne la) ; le second
 * renvoie les donnees, comme avant le port (une vue qui ecoute au `load` ne les
 * rate pas). TOUT CHARGEMENT AU-DELA est une page qui n'est plus la notre : la
 * liaison COUPE, ferme le port et ne repart plus (`'coupe'` est terminal). Un
 * navigateur qui ne compterait pas le second chargement ne perd rien : la page
 * arrivee par navigation n'a pas le port, et l'envoi s'y perd.
 */
export function lierLeCadre(cadre: CadreALier): LiaisonDuCadre {
  let etat: EtatDeLiaison = 'attente';
  let port: MessagePort | null = null;
  let chargements = 0;
  const donnerLesDonnees = (): void => {
    if (etat !== 'donne' || port === null) return;
    const { donnees, theme } = cadre.lire();
    port.postMessage(messageDeDonnees(donnees, theme));
  };
  const couper = (): void => {
    if (etat === 'coupe') return;
    etat = 'coupe';
    port?.close();
    port = null;
  };
  const annonce = (): void => {
    if (etat !== 'attente') return;
    const fenetre = cadre.fenetre();
    if (fenetre == null) return;
    const canal = (cadre.canal ?? (() => new MessageChannel()))();
    port = canal.port1;
    etat = 'donne';
    /* LE SEUL ENVOI A `'*'` : le code de la vue, jamais la fiche. L'origine du
       cadre est opaque, aucune origine nommee ne l'atteindrait. */
    fenetre.postMessage(messageDeDocument(cadre.lire().html), '*', [canal.port2]);
    donnerLesDonnees();
  };
  return {
    annonce,
    charge: () => {
      chargements += 1;
      if (chargements === 1) annonce();
      else if (chargements === 2) donnerLesDonnees();
      else if (etat !== 'coupe') {
        couper();
        cadre.signaler?.(RAISON_CADRE_COUPE);
      }
    },
    envoyer: donnerLesDonnees,
    fermer: couper,
    etat: () => etat,
  };
}

export interface VueHtmlIsoleeProps {
  /** Le document de la vue, tel que deposee. */
  html: string;
  /** Ce que la vue recoit (la fiche et ses fiches liees, reduites aux champs declares). */
  donnees: unknown;
  /** Les variables CSS a passer ; absent : `themeDuSocle()`. */
  theme?: Record<string, string>;
  /** Le nom du cadre pour les lecteurs d'ecran (le libelle de la vue). */
  titre: string;
  /** La page du cadre ; absent : `ADRESSE_DU_CADRE_VUE_HTML`. */
  adresse?: string;
}

/**
 * LE CADRE, qui remplit la zone 5. Un autre `html` remonte le cadre (`key`) :
 * une page neuve, une liaison neuve, qui redemande son document.
 */
export function VueHtmlIsolee(props: VueHtmlIsoleeProps): JSX.Element {
  return <CadreDeVue key={props.html} {...props} />;
}

function CadreDeVue({ html, donnees, theme, titre, adresse = ADRESSE_DU_CADRE_VUE_HTML }: VueHtmlIsoleeProps): JSX.Element {
  const cadre = useRef<HTMLIFrameElement | null>(null);
  /* Les dernieres donnees, lues au moment d'envoyer : un `pret` arrive entre
     deux rendus envoie ce qui est a l'ecran, pas ce qui l'etait. */
  const courant = useRef({ html, donnees, theme });
  courant.current = { html, donnees, theme };
  /* UNE LIAISON PAR MONTAGE DE L'ECOUTE, pas par rendu : un double montage
     (le mode strict de React) ferme la premiere et en ouvre une neuve, au lieu
     de laisser une liaison coupee servir le cadre. */
  const liaisonRef = useRef<LiaisonDuCadre | null>(null);

  useEffect(() => {
    /* Avant que le cadre ait recu son port, rien a envoyer : la liaison le
       sait, et son annonce donnera document et donnees. */
    liaisonRef.current?.envoyer();
  }, [donnees, theme]);

  useEffect(() => {
    const liaison = lierLeCadre({
      fenetre: () => cadre.current?.contentWindow,
      lire: () => {
        const { html: h, donnees: d, theme: t } = courant.current;
        return { html: h, donnees: d, theme: t ?? themeDuSocle() };
      },
      signaler: (texte) => console.warn(texte),
    });
    liaisonRef.current = liaison;
    const ecouter = (event: MessageEvent): void => {
      if (event.source !== cadre.current?.contentWindow || event.source == null) return;
      if (estUnMessageCadre(event.data)) liaison.annonce();
      else if (estUnMessagePret(event.data)) liaison.envoyer();
    };
    window.addEventListener('message', ecouter);
    return () => {
      window.removeEventListener('message', ecouter);
      liaison.fermer();
      if (liaisonRef.current === liaison) liaisonRef.current = null;
    };
  }, []);

  return (
    <iframe
      ref={cadre}
      title={titre}
      sandbox="allow-scripts"
      referrerPolicy="no-referrer"
      src={adresse}
      onLoad={() => liaisonRef.current?.charge()}
      className="block h-full w-full border-0 bg-surface"
    />
  );
}
